import { Ajax } from "../../shared/ajax";
import { ApiUserData, ApiTeam, ApiUser, ApiBQTable, ApiPage, ApiPageData } from "./api_data";
import { div, anchor, input } from "../../shared/dom";
import { Router } from '../../shared/router';
import { Editor } from "./editor";
import { EditorPage } from "./editor_page";
import { FilesPage } from "./files_page";
import { Page, VizConnection } from "./page";
import { Display } from "./display";
import { IndexPage } from "./index_page";
import { SVG } from "../../shared/svg";
import { Auth } from "./auth";

export interface PivotMeasure {
  id: string;
  column: string;
}

export interface BQData {
  project_id: string;
  dataset_id: string;
  table_id: string;
  column_name: string;
  column_type: string;
  rows: any[];

  pivoted: boolean;
  pivot_facet: string | null; // day of week, etc for date pivots
  pivot: string | null; // pivot match, for ex. "Monday" for dates, or "Ride" for strings
  pivot_op: string | null; // Sum or null
  pivot_target: string | null; // target column for sum
  pivot_measures: PivotMeasure[]; // list of selected target columns

  operation: string | null;

  loading: boolean;
  hidden: boolean;
  closed: boolean;
}

enum View {
  Login,
  SignupActivate,
  Index,
  Files,
  Editor,
  Page,
}

export class App {

  static view: View = View.Index;
  static router = new Router();
  static user: ApiUser | null = null;
  static teams: ApiTeam[] = [];
  static selected_owner_id: string = "";

  static tables: ApiBQTable[] = [];
  static table_sets: { [id: string]: ApiBQTable[] } = {};
  static page_sets: { [id: string]: ApiPage[] } = {};

  static selected_page: Page | null = null;

  static input_focused = false;
  static thumbnail_dirty_count = 0;
  static new_graph_data = false;
  static updating_count = 0;

  static translate_x = 0;
  static translate_y = 0;
  static zoom = 1;

  static navigate(href: string) {
    history.pushState({}, "", href);
    App.router.match();
  }

  static enable_link(a: HTMLAnchorElement) {
    a.onclick = function (e) {
      if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        App.navigate(a.href);
      }
    }
  }

  static build_table_sets(data: ApiBQTable[]) {
    App.table_sets = {};
    for (const t of data) {
      if (App.table_sets[t.owner_id] === undefined) {
        App.table_sets[t.owner_id] = [];
      }
      App.table_sets[t.owner_id].push(t);
    }
  }

  static build_page_sets(data: ApiPage[]) {
    App.page_sets = {};
    for (const p of data) {
      if (App.page_sets[p.owner_id] === undefined) {
        App.page_sets[p.owner_id] = [];
      }
      App.page_sets[p.owner_id].push(p);
    }
  }

  static input(...node_class: string[]): HTMLInputElement {
    const i = input(...node_class);
    i.addEventListener('focus', () => {
      App.input_focused = true;
    });
    i.addEventListener('blur', () => {
      App.input_focused = false;
    });
    return i;
  }

  static render(...args: string[]) {
    let header = document.querySelector("div.fixed-header") as HTMLElement;
    if (!header) {
      header = div("fixed-header");
      document.body.appendChild(header);
      const logo = div("logo");
      const a = anchor();
      a.href = "/";
      a.appendChild(SVG.logo());
      logo.appendChild(a);
      header.appendChild(logo);
    }
    let nav = document.querySelector("div.nav") as HTMLElement;
    if (!nav) {
      nav = div("nav");
      header.appendChild(nav);
    }
    let left = document.querySelector("div.left") as HTMLElement;
    if (!left) {
      left = div("left");
      document.body.appendChild(left);
    }
    let main = document.querySelector("div.main") as HTMLElement;
    if (!main) {
      main = div("main");
      document.body.appendChild(main);
    }

    // left rail gets replaced on each render
    left.innerHTML = "";

    let index = document.querySelector("div.index") as HTMLElement;
    if (!index) {
      index = div("index");
      document.body.appendChild(index);
    }

    let files = document.querySelector("div.viz-files") as HTMLElement;
    if (!files) {
      files = div("viz-files");
      main.appendChild(files);
    }

    let editor = document.querySelector("div.editor") as HTMLElement;
    if (!editor) {
      editor = div("editor");
      main.appendChild(editor);
    }

    header.style.display = "none";
    left.style.display = "none";
    main.style.display = "none";
    index.style.display = "none";
    files.style.display = "none";
    editor.style.display = "none";

    switch (App.view) {
      case View.Login:
        index.style.display = "block";
        Auth.render_login(index);
        break;

      case View.SignupActivate:
        index.style.display = "block";
        const token = args.length > 0 ? args[0] : "";
        Auth.render_activate(index, token);
        break;

      case View.Index:
        header.style.display = "flex";
        index.style.display = "block";
        IndexPage.render_main(index);
        IndexPage.render_nav(nav);
        break;

      case View.Files:
        header.style.display = "flex";
        left.style.display = "block";
        main.style.display = "block";
        files.style.display = "block";
        FilesPage.render_left_rail(left);
        FilesPage.render_main(files);
        FilesPage.render_nav(nav);
        break;

      case View.Page:
        header.style.display = "flex";
        index.style.display = "block";
        Page.render_main(index);
        Page.render_nav(nav);
        break;

      default:
        header.style.display = "flex";
        left.style.display = "block";
        main.style.display = "block";
        editor.style.display = "block";
        EditorPage.render_left_rail(left);
        EditorPage.render_main(editor);
        EditorPage.render_nav(nav);
        break;
    }

    if (App.selected_page) {
      const run = function (v: VizConnection) {
        Display.build_query_columns(v.viz);
        if (v.viz.data_changes) {
          Editor.run(v.viz);
          v.viz.data_changes = false;
        }
      }
      for (const v of App.selected_page.viz_list) {
        run(v);
      }
    }
  }

  static get_user_data(cb: Function) {
    Ajax.get("/api/v1/user_data", function (data: ApiUserData) {
      console.log(data);
      App.user = data.user;
      App.teams = data.teams;
      App.tables = data.tables;
      App.build_table_sets(data.tables);
      App.build_page_sets(data.pages);
      cb();
    }, function (status: number, _data: any) {
      if (status === 401) {
        console.log("no user");
      }
      cb();
    })
  }

  static load_files(team_id?: string) {
    const cb = function () {
      if (!App.user) {
        App.view = View.Index;
        App.render();
        return;
      }
      let id = "";
      if (team_id !== undefined && team_id !== "") {
        id = team_id;
      } else if (App.user) {
        id = App.user.id;
      }
      App.selected_owner_id = id;
      App.view = View.Files;
      App.render();
    }

    App.get_user_data(function () {
      cb();
    });
  }

  static load_editor(page_id?: string) {
    const cb = function () {
      if (!App.user) {
        App.view = View.Index;
        App.render();
        return;
      }
      let owner_id = "";
      for (const k in App.page_sets) {
        if (App.page_sets.hasOwnProperty(k)) {
          const set = App.page_sets[k];
          for (const api_page of set) {
            if (api_page.id === page_id) {
              const page = Page.parse(api_page, App.tables);
              App.selected_page = page;
              owner_id = page.owner_id;
              break;
            }
          }
        }
      }

      if (App.user && owner_id === "") {
        owner_id = App.user.id;
      }
      App.selected_owner_id = owner_id;
      App.view = View.Editor;
      App.render();
      Editor.zoom_to_fit();
      Editor.run();

      if (App.user && App.selected_owner_id !== App.user.id) {
        const home_link = document.querySelector("div.logo a") as HTMLLinkElement;
        if (home_link) {
          home_link.href = "/team/" + App.selected_owner_id;
        }
      }
    }

    App.get_user_data(function () {
      cb();
    });
  }

  static load_login() {
    App.view = View.Login;
    App.render();
  }

  static load_signup(token?: string) {
    if (token !== undefined) {
      App.view = View.SignupActivate;
      App.render(token);
      return;
    }
    App.render();
  }

  static load_page(page_id?: string) {
    const cb = function () {
      App.view = View.Page;
      App.render();
    }

    Ajax.get("/api/v1/pages/" + page_id, function (data: ApiPageData) {
      if (data.page) {
        const page = Page.parse(data.page, data.tables);
        Page.page = page;
      }
      if (data.user) {
        App.user = data.user;
      }
      cb();
    }, function (_status: number, _data: any) {
      cb();
    })
  }

  static start() {
    App.router.add("/", App.load_files);
    App.router.add("/team/:team_id", App.load_files);
    App.router.add("/editor/:page_id", App.load_editor);
    App.router.add("/login", App.load_login);
    App.router.add("/signup/:token", App.load_signup);
    App.router.add("/page/:page_id", App.load_page);
    App.router.add_not_found_route(App.load_files);

    window.addEventListener('popstate', function () {
      App.router.match();
    });

    App.router.match();
    console.log("git: __GIT_REV__");
  }
}

window.addEventListener("load", function () {
  App.start();

  document.addEventListener("keydown", function (e) {
    if (e.keyCode === 8 || e.keyCode === 46) { // delete
      Editor.delete_selected_viz();
    } else if (e.keyCode === 187) { // +
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
      Editor.zoom_in();
    } else if (e.keyCode === 189) { // -
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
      Editor.zoom_out();
    } else if (e.keyCode === 48) { // 0
      Editor.zoom_reset();
    } else if (e.keyCode === 49) { // 1
      Editor.zoom_to_fit();
    }
  })
})

window.addEventListener("resize", function () {
  if (App.view === View.Index) {
    App.render();
  }

  EditorPage.set_transform_origin();
});